import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StaleTime } from '@common/ts/enums';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import { API_ENDPOINTS } from '@/api-endpoints';

// ── Types ─────────────────────────────────────────────────────────────

export interface ResearchThread {
  _id: string;
  title: string;
  updatedAt: string;
  createdAt: string;
  message_count: number;
}

export interface ResearchMessage {
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any;
  created_at: string;
}

export interface ResearchArtifact {
  artifact_id: string;
  type:
    | 'quote_collection'
    | 'data_table'
    | 'theme_summary'
    | 'bar_chart'
    | 'grouped_bar_chart'
    | 'markdown_report'
    | 'word_cloud';
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  created_at: string;
  /** The interview question this artifact is based on (for disambiguation). */
  source_question?: string;
  /**
   * Confidence tier propagated from the ComparisonResult when this artifact
   * is sourced from a comparison (get_comparison tool). Optional — absent for
   * non-comparison artifacts and legacy comparisons without the field.
   */
  confidence_tier?: 'directional' | 'indicative' | 'established';
  /**
   * Sample sizes carried alongside the tier for the N={a}+{b} label.
   * Only present when confidence_tier is populated.
   */
  sample_size_a?: number;
  sample_size_b?: number;
  /**
   * Comparison document id — used by the recompute mutation for legacy docs.
   */
  comparison_id?: string;
}

export interface ResearchThreadFull {
  _id: string;
  title: string;
  messages: ResearchMessage[];
  artifacts: ResearchArtifact[];
  working_memory: {
    active_filter_id: string | null;
    last_discussed_question_id: string | null;
    cited_respondents: number[];
    turn_count: number;
  };
  shared: boolean;
  total_input_tokens: number;
  total_output_tokens: number;
  createdAt: string;
  updatedAt: string;
}

// ── Query keys ────────────────────────────────────────────────────────
// We accept undefined here so the queryKey is stable even before the
// projectId resolves from the route. If projectId is undefined, the key
// becomes ['research-threads', '_no_project_', 'all'] — which is distinct
// from any real project key, preventing accidental query sharing.

const NO_PROJECT = '_no_project_';

const KEYS = {
  threads: (projectId: string | undefined, filterId?: string | null) =>
    ['research-threads', projectId ?? NO_PROJECT, filterId ?? 'all'] as const,
  thread: (projectId: string | undefined, threadId: string | undefined) =>
    ['research-thread', projectId ?? NO_PROJECT, threadId ?? '_no_thread_'] as const,
  analysisSchemaStatus: (projectId: string | undefined, filterId?: string | null) =>
    ['ra-analysis-schema-status', projectId ?? NO_PROJECT, filterId ?? 'all'] as const,
};

// ── Types ─────────────────────────────────────────────────────────────

/**
 * Shape of the /analysis-schema-status endpoint response.
 * - `current: true`  → project has the rich schema the RA/Compare need
 * - `current: false, reason: 'no_analysis'` → project has never been analysed
 * - `current: false, reason: 'old_schema'`  → project was analysed before the
 *   rework and needs to be re-analysed to unlock RA/Compare
 */
export interface AnalysisSchemaStatus {
  current: boolean;
  reason?: 'no_analysis' | 'old_schema';
  /** ISO timestamp of when the analysis was last completed. Used to
   *  mark RA threads created before this date as "stale". */
  analyzed_at?: string;
}

// ── Hooks ─────────────────────────────────────────────────────────────

export const useListThreads = (projectId?: string, filterId?: string | null) =>
  useQuery<ResearchThread[]>({
    queryKey: KEYS.threads(projectId, filterId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.LIST_THREADS(projectId!, filterId),
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useGetThread = (projectId?: string, threadId?: string | null) =>
  useQuery<ResearchThreadFull>({
    queryKey: KEYS.thread(projectId, threadId ?? undefined),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.GET_THREAD(projectId!, threadId!),
      );
      return data;
    },
    enabled: !!projectId && !!threadId,
    staleTime: StaleTime.LONG,
    retry: 0, // don't retry NotFound — the thread was just deleted
  });

export const useCreateThread = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filterId: string | null) => {
      const { data } = await axiosInstanceAnalyze.post(
        API_ENDPOINTS.RESEARCH_ASSISTANT.CREATE_THREAD(projectId!),
        { filter_id: filterId },
      );
      return data as { _id: string; title: string };
    },
    onSuccess: () => {
      // Invalidate ALL filter buckets for this project so the new thread
      // shows up regardless of which filter the user is viewing. Using
      // just the project key matches every (filterId) variant.
      queryClient.invalidateQueries({
        queryKey: ['research-threads', projectId],
      });
    },
  });
};

export const useDeleteThread = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (threadId: string) => {
      await axiosInstanceAnalyze.delete(
        API_ENDPOINTS.RESEARCH_ASSISTANT.DELETE_THREAD(projectId!, threadId),
      );
      return threadId;
    },
    onSuccess: (threadId) => {
      // Filter scope unknown at delete time — invalidate all thread lists
      // for THIS project (not all projects).
      queryClient.invalidateQueries({
        queryKey: ['research-threads', projectId],
      });
      // TELV1-310 / D4: also evict the per-thread cache so messages +
      // artifacts for the deleted thread don't persist in queryClient.
      // Uses KEYS.thread() for the canonical key shape; the previous
      // bug shipped invalidation on the list key only — per-thread
      // data lingered (memory leak + consistency risk).
      queryClient.removeQueries({
        queryKey: KEYS.thread(projectId, threadId),
      });
    },
  });
};

export interface SuggestedQuestion {
  category: 'explore' | 'quotes' | 'compare' | 'report';
  text: string;
}

export const useGetSuggestions = (projectId?: string, filterId?: string | null) =>
  useQuery<SuggestedQuestion[]>({
    queryKey: ['research-suggestions', projectId, filterId ?? 'all'] as const,
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.SUGGESTIONS(projectId!, filterId),
      );
      return data;
    },
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
  });

export const useUpdateThread = (projectId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      threadId,
      updates,
    }: {
      threadId: string;
      updates: { title?: string; shared?: boolean };
    }) => {
      const { data } = await axiosInstanceAnalyze.patch(
        API_ENDPOINTS.RESEARCH_ASSISTANT.UPDATE_THREAD(projectId!, threadId),
        updates,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['research-threads', projectId],
      });
      // TELV1-310 / D4 (sweep): also invalidate the per-thread cache.
      // Without this, a title rename updates the thread list but a
      // still-mounted ChatThread reading thread.title sees the stale
      // title until a page reload.
      queryClient.invalidateQueries({
        queryKey: KEYS.thread(projectId, variables.threadId),
      });
    },
  });
};

/**
 * Pre-flight check that tells the frontend whether the project's
 * overall_pages analysis has the rich schema the Research Assistant
 * and Compare features depend on.
 *
 * Returns { current: true } when the project is ready, or
 * { current: false, reason } when the frontend should prompt the
 * user to re-run analysis via the OutdatedAnalysisDialog.
 *
 * Cached aggressively — the status is stable between user actions
 * (only changes when analysis is (re-)run) so we cache for the whole
 * staleTime window and refetch on window focus to catch the case
 * where the user triggered a re-run in another tab.
 */
export const useAnalysisSchemaStatus = (projectId?: string, filterId?: string | null) =>
  useQuery<AnalysisSchemaStatus>({
    queryKey: KEYS.analysisSchemaStatus(projectId, filterId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        API_ENDPOINTS.RESEARCH_ASSISTANT.ANALYSIS_SCHEMA_STATUS(projectId!, filterId),
      );
      return data as AnalysisSchemaStatus;
    },
    enabled: !!projectId,
    staleTime: StaleTime.LONG,
    refetchOnWindowFocus: true,
    retry: 0,
  });

/**
 * Helper for components that want to invalidate the schema status
 * cache — used after a force re-run completes so the dialog sees
 * the new "current: true" state on its next read.
 */
export const useInvalidateAnalysisSchemaStatus = () => {
  const queryClient = useQueryClient();
  return (projectId?: string) => {
    // Invalidate all filter buckets for this project — we don't know
    // which filter the re-run touched so we refetch all of them.
    queryClient.invalidateQueries({
      queryKey: ['ra-analysis-schema-status', projectId ?? NO_PROJECT],
    });
  };
};

// ── D.8: Analysis run progress ────────────────────────────────────────────

/**
 * Shape returned by `GET /analyzer/run/:project_id/progress`.
 * Added in Wave A backend (D.8 prerequisite). When no run is in flight,
 * the endpoint returns `{ phase: 'idle' }`.
 */
export interface AnalysisRunProgress {
  phase: 'overall' | 'per_question_pages' | 'meta_synthesis' | 'idle' | 'complete';
  current?: number;
  total?: number;
  /** ISO 8601 timestamp when the run started */
  started_at?: string;
  elapsed_ms?: number;
  /** ISO 8601 timestamp when a completed run finished — used for closed-tab recovery (D.8 LLM-R2) */
  completed_at?: string;
}

const KEYS_EXTENDED = {
  // TELV1-429 — keyed by filter_id too so the RA can poll the progress of the
  // ACTIVE filter's re-run (legacy per-filter analysis upgrade), not only the
  // unfiltered run. Unfiltered scope → 'all' (unchanged behaviour).
  analysisRunProgress: (projectId: string | undefined, filterId?: string | null) =>
    ['analysis-run-progress', projectId ?? NO_PROJECT, filterId ?? 'all'] as const,
};

/**
 * D.8 — Polls `GET /analyzer/run/:project_id/progress` every 5 seconds
 * while a run is in flight. Returns `{ phase: 'idle' }` when no run is
 * active, and `{ phase: 'complete', completed_at }` for runs that finished
 * while the tab was closed (closed-tab recovery, D.8 LLM-R2).
 *
 * Hook signature exposed to callers:
 *   const { data, isLoading } = useAnalysisRunProgress(projectId, filterId)
 *
 * TELV1-429 — pass `filterId` to poll the active filter's run progress. Omit
 * (or pass null) for the unfiltered scope. The backend endpoint already accepts
 * `?filter_id` (runner.controller getProgress).
 */
export const useAnalysisRunProgress = (projectId?: string, filterId?: string | null) =>
  useQuery<AnalysisRunProgress>({
    queryKey: KEYS_EXTENDED.analysisRunProgress(projectId, filterId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get(
        `${API_ENDPOINTS.RUN.GET_RUN_PROGRESS(projectId)}${
          filterId ? `?filter_id=${encodeURIComponent(filterId)}` : ''
        }`,
      );
      return data as AnalysisRunProgress;
    },
    enabled: !!projectId,
    // Poll every 5s — acceptable given ETag/304 caching on the backend (D.8 Arch-R2)
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
    // On mount, immediately check so closed-tab recovery fires without waiting 5s
    staleTime: 0,
    retry: 0,
  });
